import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Custom element that watches the cart total and:
 *  - opens the cart drawer + a gift-picker dialog the moment the customer
 *    crosses the gift threshold (and hasn't picked/declined a gift yet)
 *  - adds/removes the chosen gift line item directly via the Cart AJAX API
 *  - removes the gift automatically if the cart drops back below the threshold
 *
 * The threshold is 90€ for every cart (data-gift-threshold-base /
 * data-gift-threshold-calendar, rendered by snippets/milestone-gift-bar.liquid).
 * It is measured AFTER discount codes and without counting the gift itself
 * (see #qualifyingFor), the same way Shopify decides whether the gift is free,
 * so a cart that only reaches 90€ before the code loses the gift instead of
 * being charged for it.
 *
 * Built for VeCandles, independent of any third-party app.
 */
class GiftThresholdWatcher extends HTMLElement {
  /**
   * The cart section is re-rendered by *morphing* the DOM (see
   * section-renderer.js / @theme/morph) instead of destroying and recreating
   * elements. That means whenever something else changes the cart (e.g. the
   * cross-sell "+" button, which only does its own fetch + section morph and
   * never fires the theme's cartLinesUpdate event), this element's own
   * connectedCallback/disconnectedCallback never run again — only its
   * data-* attributes get patched with the fresh values. Watching those
   * attributes directly is what keeps the gift picker working no matter
   * which part of the theme changed the cart.
   */
  static observedAttributes = [
    'data-qualifying-subtotal',
    'data-gift-in-cart',
    'data-gift-decision',
    'data-gift-threshold',
  ];

  /** Bound reference to #onCartUpdate so add/removeEventListener target the same function. */
  #boundOnCartUpdate = this.#onCartUpdate.bind(this);
  /** Guards attributeChangedCallback so it only reacts once connectedCallback has run
   *  (attributeChangedCallback fires once per attribute during initial parsing, before
   *  connectedCallback even runs — without this guard we'd evaluate twice on mount). */
  #connected = false;

  connectedCallback() {
    this.#connected = true;
    this.#evaluate();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#boundOnCartUpdate);

    // Delegated listener on `this` (the <gift-threshold-watcher> element itself, which
    // morph always reuses in place) rather than one per button — the same reasoning as
    // cart-cross-sell.js: a morph-driven re-render of the dialog's contents can replace
    // these buttons with fresh nodes that a per-button addEventListener would never reach.
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#connected = false;
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#boundOnCartUpdate);
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.#connected || oldValue === newValue) return;
    this.#evaluate();
  }

  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('#gift-picker-dialog'));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const optionButton = target.closest('[data-gift-option]');
    if (optionButton instanceof HTMLElement) {
      this.#onPick(optionButton);
      return;
    }

    if (target.closest('[data-gift-picker-decline]')) {
      this.#onDecline();
      return;
    }

    if (target.closest('[data-gift-picker-close]')) {
      this.#dialog?.close();
      return;
    }

    if (target.closest('[data-gift-change]')) {
      this.#openDialog();
    }
  };

  /**
   * Handles any cart change anywhere on the site (product page add-to-cart,
   * quantity changes in the drawer, etc.) by re-checking the authoritative
   * cart state from the server.
   * @param {any} event
   */
  #onCartUpdate(event) {
    const settle = () => this.#refreshFromServerCart();
    if (event?.promise?.then) {
      event.promise.then(settle).catch(settle);
    } else {
      settle();
    }
  }

  /**
   * Gift threshold for a given cart (same value with or without the Calendario
   * de Adviento; both come from the Liquid data-* attributes).
   * @param {any} cart
   */
  #thresholdFor(cart) {
    const base = Number(this.dataset.giftThresholdBase || 9000);
    const calendarThreshold = Number(this.dataset.giftThresholdCalendar || base);
    const calendarId = Number(this.dataset.calendarProductId || 0);
    const hasCalendar =
      Boolean(calendarId) && Array.isArray(cart?.items) && cart.items.some((item) => item.product_id === calendarId);
    return hasCalendar ? calendarThreshold : base;
  }

  /**
   * Amount that counts towards the gift: what the customer pays for everything
   * EXCEPT the gift itself, after every discount. Gift lines are left out (if the
   * gift stops being free it must not help to keep itself), and order-level
   * discounts such as the welcome code (cart_level_discount_applications) are
   * subtracted in proportion to the non-gift lines. This mirrors Shopify, which
   * only gives the gift for free when the order still reaches 90€ after the code.
   * @param {any} cart
   */
  #qualifyingFor(cart) {
    const quemadorId = Number(this.dataset.quemadorVariantId);
    const toteId = Number(this.dataset.toteVariantId);
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const subtotal = Number(cart?.items_subtotal_price || 0);
    const nonGift = items
      .filter((item) => item.variant_id !== quemadorId && item.variant_id !== toteId)
      .reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
    const cartLevel = Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications.reduce(
          (sum, discount) => sum + Number(discount?.total_allocated_amount || 0),
          0
        )
      : 0;
    const share = subtotal > 0 ? (cartLevel * nonGift) / subtotal : 0;
    return Math.max(0, Math.round(nonGift - share));
  }

  async #refreshFromServerCart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();

      const quemadorId = Number(this.dataset.quemadorVariantId);
      const toteId = Number(this.dataset.toteVariantId);

      const giftLine = cart.items.find(
        (item) => item.variant_id === quemadorId || item.variant_id === toteId
      );
      const giftKey = giftLine ? (giftLine.variant_id === quemadorId ? 'quemador' : 'tote') : '';
      const qualifying = this.#qualifyingFor(cart);

      // Threshold first, so that the evaluation triggered by the subtotal change
      // below already uses the right one.
      this.dataset.giftThreshold = String(this.#thresholdFor(cart));
      this.dataset.qualifyingSubtotal = String(qualifying);
      this.dataset.giftInCart = giftKey;
      this.dataset.giftDecision = cart.attributes?.regalo_elegido || '';

      this.#evaluate();
    } catch (error) {
      console.warn('[gift-threshold-watcher] Failed to refresh cart state:', error);
    }
  }

  #evaluate() {
    const qualifying = Number(this.dataset.qualifyingSubtotal || 0);
    const giftThreshold = Number(this.dataset.giftThreshold || 9000);
    const giftInCart = this.dataset.giftInCart || '';
    const decision = this.dataset.giftDecision || '';

    if (qualifying >= giftThreshold) {
      if (!giftInCart && decision !== 'ninguno') {
        this.#openDialog();
      }
      return;
    }

    // Below the threshold: never leave the picker open.
    if (this.#dialog?.open) this.#dialog.close();

    // Dropped back below the threshold.
    if (giftInCart) {
      // A gift is still in the cart — remove it and clear the decision so the
      // picker can trigger again if they climb back up.
      this.#removeGift();
    } else if (decision === 'ninguno') {
      // No gift in the cart, but a previous "no gracias" is still recorded.
      // Without this, someone who declines, removes items, and later adds
      // enough again to re-cross the threshold would never see the picker a
      // second time — the "ninguno" flag would silently follow the cart forever.
      // Clearing it here (client-side only; nothing to undo on the actual
      // cart) means a fresh climb past the threshold always gets a fresh
      // offer.
      this.dataset.giftDecision = '';
    }
  }

  #openDialog() {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;

    const showGiftDialog = () => {
      if (!dialog.open) dialog.showModal();
    };

    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (!drawer?.open) {
      // No drawer to coordinate with (shouldn't normally happen since this
      // component only ever renders inside the cart drawer) — just show it.
      showGiftDialog();
      return;
    }

    // theme-drawer dispatches a 'theme-drawer:open' event right after it
    // finishes calling showModal()/show() on its OWN <dialog> — both the
    // first time it opens, and when open() is called on an already-open
    // drawer (which internally closes + reopens its own dialog to bring it
    // to front). That second case is exactly what happens here when the
    // shopper already has the cart drawer open and then crosses the threshold
    // (e.g. by bumping a quantity in the drawer itself): calling drawer.open()
    // briefly closes and reopens the drawer's own <dialog>. A dialog nested
    // inside a closed (display: none) ancestor dialog can't render even if
    // told to open — so waiting for the real "the drawer's dialog is open
    // now" signal, instead of guessing with a fixed animation-frame delay,
    // is what makes this reliable whether the drawer was already open or not.
    drawer.addEventListener('theme-drawer:open', showGiftDialog, { once: true });
    drawer.open();
  }

  /** @param {HTMLElement} button */
  #onPick = async (button) => {
    const variantId = Number(button.dataset.variantId);
    const giftKey = button.dataset.giftKey || '';
    if (!variantId || !giftKey) return;

    await this.#setGift(variantId, giftKey);
    this.#dialog?.close();
  };

  #onDecline = async () => {
    await this.#updateCart({ updates: {}, attributes: { regalo_elegido: 'ninguno' } });
    this.#dialog?.close();
  };

  /**
   * @param {number} variantId
   * @param {string} giftKey
   */
  async #setGift(variantId, giftKey) {
    const quemadorId = Number(this.dataset.quemadorVariantId);
    const toteId = Number(this.dataset.toteVariantId);
    const otherId = variantId === quemadorId ? toteId : quemadorId;

    // /cart/update.js can only change the quantity of a line that's ALREADY in the
    // cart — it can never add a brand-new line. So before touching anything else,
    // check the real cart and, if this gift isn't in it yet, add it with
    // /cart/add.js first. Skipping this was the bug: picking a gift for the first
    // time (or switching to the one you don't currently have) silently did nothing.
    let alreadyInCart = false;
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      alreadyInCart = cart.items.some((item) => item.variant_id === variantId);

      // Safety net: never add a gift to a cart that doesn't reach the threshold
      // once every discount (welcome code included) has been taken off.
      if (!alreadyInCart && this.#qualifyingFor(cart) < this.#thresholdFor(cart)) {
        this.dataset.giftThreshold = String(this.#thresholdFor(cart));
        this.dataset.qualifyingSubtotal = String(this.#qualifyingFor(cart));
        return;
      }
    } catch (error) {
      console.warn('[gift-threshold-watcher] Failed to read cart before setting gift:', error);
    }

    if (!alreadyInCart) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!response.ok) {
          throw new Error(`Cart add failed with status ${response.status}`);
        }
      } catch (error) {
        console.warn('[gift-threshold-watcher] Failed to add gift to cart:', error);
        return;
      }
    }

    // Now that the chosen gift is guaranteed to be a line in the cart, remove the
    // other option (if present) and record the choice — both are safe to do via
    // /cart/update.js since they only ever touch lines that already exist.
    /** @type {Record<string, number>} */
    const updates = {};
    updates[otherId] = 0;

    await this.#updateCart({ updates, attributes: { regalo_elegido: giftKey } });
  }

  async #removeGift() {
    const quemadorId = Number(this.dataset.quemadorVariantId);
    const toteId = Number(this.dataset.toteVariantId);

    /** @type {Record<string, number>} */
    const updates = {};
    updates[quemadorId] = 0;
    updates[toteId] = 0;

    await this.#updateCart({ updates, attributes: { regalo_elegido: '' } });
  }

  /**
   * @param {{updates: Record<string, number>, attributes: Record<string, string>}} body
   */
  async #updateCart(body) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const cart = await response.json();

      /** @type {any} */
      const cartIcon = document.querySelector('cart-icon');
      cartIcon?.renderCartBubble?.(cart.item_count);

      const sectionId = this.dataset.cartDrawerSectionId || 'cart-drawer-section';
      // injectStylesheet: true keeps the section's <style> in sync with whatever this
      // fresh render actually needs, the same defensive step the theme's own
      // cart-items-component takes whenever it re-renders this section.
      await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration', injectStylesheet: true });

      return cart;
    } catch (error) {
      console.warn('[gift-threshold-watcher] Failed to update cart:', error);
      return null;
    }
  }
}

if (!customElements.get('gift-threshold-watcher')) {
  customElements.define('gift-threshold-watcher', GiftThresholdWatcher);
}
